import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pseoAPI, categoriesAPI, uploadAPI, brandCategoriesAPI } from '../utils/api';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, ShoppingBag, Gift, Layers, 
  X, Save, Upload, Tag, Calendar, ChevronDown, ChevronUp,
  Eye, ExternalLink
} from 'lucide-react';

// Tab names
const TABS = ['brands', 'categories', 'sale-events'];

// Default color palette
const COLORS = [
  '#E63946', '#EE4D2D', '#FF6B00', '#1D3D2D', '#2B5F9E',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#6B7280'
];

export const AdminPSEO = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('brands');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [brands, setBrands] = useState([]);
  const [saleEvents, setSaleEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brandCategories, setBrandCategories] = useState([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingItem, setEditingItem] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({});
  
  // Deactivate modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingCategory, setDeactivatingCategory] = useState(null);
  const [reassignCategoryId, setReassignCategoryId] = useState('');
  const [categoryArticleCount, setCategoryArticleCount] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [brandsRes, eventsRes, categoriesRes, brandCatsRes] = await Promise.all([
        pseoAPI.getBrands(),
        pseoAPI.getSaleEvents(),
        categoriesAPI.getAll(true),
        brandCategoriesAPI.getAll()
      ]);
      setBrands(brandsRes.data || []);
      setSaleEvents(eventsRes.data || []);
      setCategories(categoriesRes.data || []);
      setBrandCategories(brandCatsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch pSEO data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditingItem(null);
    setFormData(getDefaultFormData());
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (item) => {
    setModalMode('edit');
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  // Get default form data based on active tab
  const getDefaultFormData = () => {
    if (activeTab === 'brands') {
      return {
        name: '',
        slug: '',
        description: '',
        short_description: '',
        logo: '',
        color: '#E63946',
        category: 'E-Commerce',
        is_popular: false,
        is_featured: false,
        meta_title: '',
        meta_description: '',
        faqs: [],
        related_brands: []
      };
    } else if (activeTab === 'sale-events') {
      return {
        name: '',
        slug: '',
        description: '',
        short_description: '',
        banner_image: '',
        start_date: '',
        end_date: '',
        is_active: true,
        meta_title: '',
        meta_description: '',
        faqs: [],
        related_brands: [],
        related_categories: []
      };
    } else {
      return {
        name: '',
        slug: '',
        description: '',
        banner_image: '',
        meta_title: '',
        meta_description: ''
      };
    }
  };

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Handle form field change
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name' && modalMode === 'create') {
      const baseSlug = generateSlug(value);
      const suffix = activeTab === 'brands' ? '-deals' : 
                     activeTab === 'sale-events' ? '' : '-deals';
      setFormData(prev => ({ ...prev, slug: baseSlug + suffix }));
    }
  };

  // Handle image upload
  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const response = await uploadAPI.upload(file);
      handleFieldChange(field, response.data.url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  // Add FAQ
  const addFAQ = () => {
    setFormData(prev => ({
      ...prev,
      faqs: [...(prev.faqs || []), { question: '', answer: '' }]
    }));
  };

  // Update FAQ
  const updateFAQ = (index, field, value) => {
    setFormData(prev => {
      const newFaqs = [...(prev.faqs || [])];
      newFaqs[index] = { ...newFaqs[index], [field]: value };
      return { ...prev, faqs: newFaqs };
    });
  };

  // Remove FAQ
  const removeFAQ = (index) => {
    setFormData(prev => ({
      ...prev,
      faqs: (prev.faqs || []).filter((_, i) => i !== index)
    }));
  };

  // Save item
  const handleSave = async () => {
    try {
      if (!formData.name || !formData.slug) {
        toast.error('Name and slug are required');
        return;
      }

      if (activeTab === 'brands') {
        if (modalMode === 'create') {
          await pseoAPI.createBrand(formData);
          toast.success('Brand created successfully');
        } else {
          await pseoAPI.updateBrand(editingItem.id, formData);
          toast.success('Brand updated successfully');
        }
      } else if (activeTab === 'sale-events') {
        if (modalMode === 'create') {
          await pseoAPI.createSaleEvent(formData);
          toast.success('Sale event created successfully');
        } else {
          await pseoAPI.updateSaleEvent(editingItem.id, formData);
          toast.success('Sale event updated successfully');
        }
      } else if (activeTab === 'categories') {
        if (modalMode === 'edit') {
          await categoriesAPI.update(editingItem.id, formData);
          toast.success('Category updated successfully');
        }
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  // Delete item
  const handleDelete = async (item) => {
    const itemName = item.name;
    if (!window.confirm(`Are you sure you want to delete "${itemName}"?`)) return;

    try {
      if (activeTab === 'brands') {
        await pseoAPI.deleteBrand(item.id);
      } else if (activeTab === 'sale-events') {
        await pseoAPI.deleteSaleEvent(item.id);
      }
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Open deactivate modal for category
  const openDeactivateModal = async (category) => {
    setDeactivatingCategory(category);
    setReassignCategoryId('');
    try {
      const response = await categoriesAPI.getArticleCount(category.id);
      setCategoryArticleCount(response.data.article_count);
    } catch (error) {
      setCategoryArticleCount(0);
    }
    setShowDeactivateModal(true);
  };

  // Deactivate category
  const handleDeactivateCategory = async () => {
    if (!deactivatingCategory) return;
    
    try {
      await categoriesAPI.deactivate(
        deactivatingCategory.id, 
        reassignCategoryId || null
      );
      toast.success(`Category "${deactivatingCategory.name}" deactivated successfully`);
      setShowDeactivateModal(false);
      setDeactivatingCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate category');
    }
  };

  // Reactivate category
  const handleReactivateCategory = async (category) => {
    try {
      await categoriesAPI.activate(category.id);
      toast.success(`Category "${category.name}" reactivated successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to reactivate category');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-brand-red mx-auto mb-4"></div>
          <p className="text-gray-600 font-inter">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-pseo-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-playfair font-bold text-brand-green mb-2">pSEO Management</h1>
            <p className="text-gray-600 font-inter">Manage brands, categories, and sale events for programmatic SEO pages</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 font-bold hover:bg-[#D62828] transition-colors uppercase tracking-wide rounded-lg"
            data-testid="create-pseo-button"
          >
            <Plus className="w-5 h-5" />
            New {activeTab === 'brands' ? 'Brand' : activeTab === 'sale-events' ? 'Event' : 'Category'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b-2 border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-semibold capitalize transition-colors flex items-center gap-2 border-b-2 -mb-[2px] ${
                activeTab === tab
                  ? 'text-brand-red border-brand-red'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'brands' && <ShoppingBag className="w-5 h-5" />}
              {tab === 'categories' && <Layers className="w-5 h-5" />}
              {tab === 'sale-events' && <Gift className="w-5 h-5" />}
              {tab.replace('-', ' ')}
              <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                {tab === 'brands' ? brands.length : tab === 'sale-events' ? saleEvents.length : categories.length}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'brands' && (
          <BrandsTable 
            brands={brands} 
            onEdit={openEditModal} 
            onDelete={handleDelete} 
          />
        )}
        {activeTab === 'sale-events' && (
          <SaleEventsTable 
            events={saleEvents} 
            onEdit={openEditModal} 
            onDelete={handleDelete} 
          />
        )}
        {activeTab === 'categories' && (
          <CategoriesTable 
            categories={categories} 
            onEdit={openEditModal}
            onDeactivate={openDeactivateModal}
            onReactivate={handleReactivateCategory}
          />
        )}

        {/* Deactivate Category Modal */}
        {showDeactivateModal && deactivatingCategory && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDeactivateModal(false)}>
            <div 
              className="bg-white rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="deactivate-category-modal"
            >
              <h2 className="text-xl font-playfair font-bold text-brand-green mb-4">
                Deactivate Category
              </h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to deactivate <strong>"{deactivatingCategory.name}"</strong>?
              </p>
              
              {categoryArticleCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800 font-medium mb-2">
                    ⚠️ This category has {categoryArticleCount} article{categoryArticleCount > 1 ? 's' : ''}.
                  </p>
                  <p className="text-sm text-yellow-700 mb-3">
                    Would you like to reassign them to another category?
                  </p>
                  <select
                    value={reassignCategoryId}
                    onChange={(e) => setReassignCategoryId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  >
                    <option value="">Leave articles in this category</option>
                    {categories
                      .filter(c => c.id !== deactivatingCategory.id && c.is_active !== false)
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}
              
              <p className="text-sm text-gray-500 mb-6">
                Deactivated categories are hidden from the frontend but can be reactivated later.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={handleDeactivateCategory}
                  className="flex-1 bg-brand-red text-white py-2 px-4 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
                >
                  Deactivate
                </button>
                <button
                  onClick={() => setShowDeactivateModal(false)}
                  className="flex-1 border border-gray-300 py-2 px-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <Modal
            activeTab={activeTab}
            modalMode={modalMode}
            formData={formData}
            onFieldChange={handleFieldChange}
            onImageUpload={handleImageUpload}
            onSave={handleSave}
            onClose={() => setShowModal(false)}
            addFAQ={addFAQ}
            updateFAQ={updateFAQ}
            removeFAQ={removeFAQ}
            brands={brands}
            brandCategories={brandCategories}
          />
        )}
      </div>
    </div>
  );
};

// Brands Table Component
const BrandsTable = ({ brands, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="brands-table">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Brand</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Category</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Slug</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Status</th>
            <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {brands.map((brand) => (
            <tr key={brand.id} className="hover:bg-gray-50" data-testid={`brand-row-${brand.id}`}>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  {brand.logo ? (
                    <img 
                      src={brand.logo} 
                      alt={`${brand.name} logo`}
                      className="w-10 h-10 rounded-lg object-contain bg-white border p-1"
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: brand.color || '#E63946' }}
                    >
                      {brand.name?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-gray-900">{brand.name}</div>
                    <div className="text-sm text-gray-500 line-clamp-1">{brand.short_description || brand.description?.slice(0, 50)}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm text-gray-700">
                  <Tag className="w-3 h-3" />
                  {brand.category}
                </span>
              </td>
              <td className="px-6 py-4">
                <code className="text-sm text-brand-red bg-red-50 px-2 py-1 rounded">/{brand.slug}</code>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  {brand.is_featured && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">Featured</span>
                  )}
                  {brand.is_popular && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Popular</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  <a 
                    href={`/${brand.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-brand-green transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => onEdit(brand)} className="p-2 text-gray-500 hover:text-brand-green transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(brand)} className="p-2 text-gray-500 hover:text-brand-red transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {brands.length === 0 && (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                No brands found. Create your first brand to get started.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Sale Events Table Component
const SaleEventsTable = ({ events, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="sale-events-table">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Event</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Slug</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Dates</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Status</th>
            <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {events.map((event) => (
            <tr key={event.id} className="hover:bg-gray-50" data-testid={`event-row-${event.id}`}>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-red flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{event.name}</div>
                    <div className="text-sm text-gray-500 line-clamp-1">{event.short_description || event.description?.slice(0, 50)}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <code className="text-sm text-brand-red bg-red-50 px-2 py-1 rounded">/{event.slug}</code>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  {event.start_date || 'N/A'} - {event.end_date || 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                  event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {event.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  <a 
                    href={`/${event.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-brand-green transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => onEdit(event)} className="p-2 text-gray-500 hover:text-brand-green transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(event)} className="p-2 text-gray-500 hover:text-brand-red transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {events.length === 0 && (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                No sale events found. Create your first event to get started.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Categories Table Component
const CategoriesTable = ({ categories, onEdit, onDeactivate, onReactivate }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="categories-table">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Category</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Slug</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Banner</th>
            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Status</th>
            <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {categories.map((category) => (
            <tr 
              key={category.id} 
              className={`hover:bg-gray-50 ${category.is_active === false ? 'opacity-60 bg-gray-50' : ''}`} 
              data-testid={`category-row-${category.id}`}
            >
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.is_active === false ? 'bg-gray-400' : 'bg-brand-green'}`}>
                    <Layers className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{category.name}</div>
                    <div className="text-sm text-gray-500">{category.description?.slice(0, 50) || 'No description'}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <code className={`text-sm px-2 py-1 rounded ${category.is_active === false ? 'text-gray-500 bg-gray-100' : 'text-brand-green bg-green-50'}`}>
                  /category/{category.slug}
                </code>
              </td>
              <td className="px-6 py-4">
                {category.banner_image ? (
                  <img src={category.banner_image} alt="" className="w-20 h-12 object-cover rounded" />
                ) : (
                  <span className="text-sm text-gray-400">No banner</span>
                )}
              </td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                  category.is_active === false 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {category.is_active === false ? 'Inactive' : 'Active'}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  <a 
                    href={`/category/${category.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-brand-green transition-colors"
                    title="View page"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button 
                    onClick={() => onEdit(category)} 
                    className="p-2 text-gray-500 hover:text-brand-green transition-colors"
                    title="Edit category"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {category.is_active === false ? (
                    <button 
                      onClick={() => onReactivate(category)} 
                      className="p-2 text-gray-500 hover:text-green-600 transition-colors"
                      title="Reactivate category"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  ) : (
                    <button 
                      onClick={() => onDeactivate(category)} 
                      className="p-2 text-gray-500 hover:text-brand-red transition-colors"
                      title="Deactivate category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {categories.length === 0 && (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                No categories found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Modal Component
const Modal = ({ 
  activeTab, 
  modalMode, 
  formData, 
  onFieldChange, 
  onImageUpload, 
  onSave, 
  onClose,
  addFAQ,
  updateFAQ,
  removeFAQ,
  brands,
  brandCategories
}) => {
  const [showFAQs, setShowFAQs] = useState(false);
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="pseo-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-playfair font-bold text-brand-green">
            {modalMode === 'create' ? 'Create' : 'Edit'} {
              activeTab === 'brands' ? 'Brand' : 
              activeTab === 'sale-events' ? 'Sale Event' : 'Category'
            }
          </h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => onFieldChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
              placeholder={activeTab === 'brands' ? 'e.g., Shopee' : activeTab === 'sale-events' ? 'e.g., Black Friday Deals' : 'Category name'}
              data-testid="input-name"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">URL Slug *</label>
            <div className="flex items-center">
              <span className="bg-gray-100 px-4 py-3 border border-r-0 border-gray-300 rounded-l-lg text-gray-500">/</span>
              <input
                type="text"
                value={formData.slug || ''}
                onChange={(e) => onFieldChange('slug', e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                placeholder="shopee-deals"
                data-testid="input-slug"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => onFieldChange('description', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent resize-none"
              placeholder="A brief description..."
              data-testid="input-description"
            />
          </div>

          {/* Short Description */}
          {(activeTab === 'brands' || activeTab === 'sale-events') && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Short Description</label>
              <input
                type="text"
                value={formData.short_description || ''}
                onChange={(e) => onFieldChange('short_description', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                placeholder="A brief tagline"
                data-testid="input-short-description"
              />
            </div>
          )}

          {/* Brand-specific fields */}
          {activeTab === 'brands' && (
            <>
              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
                <select
                  value={formData.category || 'E-Commerce'}
                  onChange={(e) => onFieldChange('category', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  data-testid="input-category"
                >
                  {brandCategories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Brand Color</label>
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    {COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => onFieldChange('color', color)}
                        className={`w-8 h-8 rounded-full border-2 transition-transform ${
                          formData.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <input
                    type="text"
                    value={formData.color || '#E63946'}
                    onChange={(e) => onFieldChange('color', e.target.value)}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>

              {/* Brand Logo (Square) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Brand Logo <span className="text-gray-400 font-normal">(Square icon, shown next to brand name)</span>
                </label>
                <div className="flex gap-4 items-center">
                  {formData.logo && (
                    <img 
                      src={formData.logo} 
                      alt="Logo" 
                      className="w-16 h-16 object-contain rounded-lg border bg-white p-1" 
                    />
                  )}
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.logo || ''}
                      onChange={(e) => onFieldChange('logo', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                      placeholder="Logo URL or upload"
                      data-testid="input-logo"
                    />
                  </div>
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 p-3 rounded-lg transition-colors" title="Upload logo">
                    <Upload className="w-5 h-5 text-gray-600" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageUpload(e, 'logo')}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">Recommended: Square image (e.g., 200x200px). Auto-optimized to WebP.</p>
              </div>

              {/* Hero Banner Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Hero Banner <span className="text-gray-400 font-normal">(Background for brand page header)</span>
                </label>
                <div className="space-y-2">
                  {formData.banner_image && (
                    <img src={formData.banner_image} alt="Banner" className="w-full h-32 object-cover rounded-lg" />
                  )}
                  <div className="flex gap-4">
                    <input
                      type="text"
                      value={formData.banner_image || ''}
                      onChange={(e) => onFieldChange('banner_image', e.target.value)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                      placeholder="Banner image URL or upload"
                      data-testid="input-banner-image"
                    />
                    <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 p-3 rounded-lg transition-colors" title="Upload banner">
                      <Upload className="w-5 h-5 text-gray-600" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onImageUpload(e, 'banner_image')}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Recommended: Wide image (e.g., 1920x400px)</p>
                </div>
              </div>

              {/* Popular / Featured flags */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_popular || false}
                    onChange={(e) => onFieldChange('is_popular', e.target.checked)}
                    className="w-4 h-4 text-brand-red border-gray-300 rounded focus:ring-brand-red"
                  />
                  <span className="text-sm text-gray-700">Popular Brand</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured || false}
                    onChange={(e) => onFieldChange('is_featured', e.target.checked)}
                    className="w-4 h-4 text-brand-red border-gray-300 rounded focus:ring-brand-red"
                  />
                  <span className="text-sm text-gray-700">Featured</span>
                </label>
              </div>
            </>
          )}

          {/* Sale Event specific fields */}
          {activeTab === 'sale-events' && (
            <>
              {/* Banner Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Image</label>
                <div className="space-y-2">
                  {formData.banner_image && (
                    <img src={formData.banner_image} alt="Banner" className="w-full h-32 object-cover rounded-lg" />
                  )}
                  <div className="flex gap-4">
                    <input
                      type="text"
                      value={formData.banner_image || ''}
                      onChange={(e) => onFieldChange('banner_image', e.target.value)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                      placeholder="Banner image URL"
                    />
                    <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 p-3 rounded-lg transition-colors">
                      <Upload className="w-5 h-5 text-gray-600" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onImageUpload(e, 'banner_image')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => onFieldChange('start_date', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => onFieldChange('end_date', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  />
                </div>
              </div>

              {/* Active flag */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => onFieldChange('is_active', e.target.checked)}
                  className="w-4 h-4 text-brand-red border-gray-300 rounded focus:ring-brand-red"
                />
                <span className="text-sm text-gray-700">Active (visible on site)</span>
              </label>
            </>
          )}

          {/* Category specific - Banner Image */}
          {activeTab === 'categories' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Image</label>
              <div className="space-y-2">
                {formData.banner_image && (
                  <img src={formData.banner_image} alt="Banner" className="w-full h-32 object-cover rounded-lg" />
                )}
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={formData.banner_image || ''}
                    onChange={(e) => onFieldChange('banner_image', e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                    placeholder="Banner image URL"
                  />
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 p-3 rounded-lg transition-colors">
                    <Upload className="w-5 h-5 text-gray-600" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageUpload(e, 'banner_image')}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* SEO Fields */}
          <div className="border-t pt-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Title</label>
                <input
                  type="text"
                  value={formData.meta_title || ''}
                  onChange={(e) => onFieldChange('meta_title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  placeholder="Custom page title for search engines"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Description</label>
                <textarea
                  value={formData.meta_description || ''}
                  onChange={(e) => onFieldChange('meta_description', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-transparent resize-none"
                  placeholder="Description shown in search results (150-160 characters)"
                />
              </div>
            </div>
          </div>

          {/* FAQs Section */}
          {(activeTab === 'brands' || activeTab === 'sale-events') && (
            <div className="border-t pt-6">
              <button
                type="button"
                onClick={() => setShowFAQs(!showFAQs)}
                className="flex items-center justify-between w-full text-left"
              >
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                  FAQs ({formData.faqs?.length || 0})
                </h3>
                {showFAQs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showFAQs && (
                <div className="mt-4 space-y-4">
                  {(formData.faqs || []).map((faq, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => removeFAQ(index)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <input
                        type="text"
                        value={faq.question || ''}
                        onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded mb-2 text-sm"
                        placeholder="Question"
                      />
                      <textarea
                        value={faq.answer || ''}
                        onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none"
                        placeholder="Answer"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFAQ}
                    className="text-brand-red hover:text-brand-red/80 text-sm font-semibold flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add FAQ
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-6 py-2 bg-brand-red text-white rounded-lg hover:bg-brand-red/90 transition-colors flex items-center gap-2"
            data-testid="save-button"
          >
            <Save className="w-4 h-4" />
            {modalMode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPSEO;
